/**
 * Page manager screen features
 * @module services/pm-screen
 */
import { Map, List } from 'immutable'
import _get from 'lodash/get'
import { get as getConfig } from 'config'
import {
  isMemberHome,
  isYogaPage,
  isTopics,
  isEventsPage,
  isHubsAlternativeHealth,
  isHubsAncientWisdom,
  isHubsPersonalDevelopment,
} from 'services/url'
import { getAuthIsLoggedIn } from 'services/auth'
import {
  SCREEN_TYPE_HUBS_PERSONAL_DEVELOPMENT,
  SCREEN_TYPE_HUBS_ANCIENT_WISDOM,
  SCREEN_TYPE_HUBS_ALTERNATIVE_HEALTH,
} from 'services/page-manager/entities'
import {
  PM_SCREEN_TYPE_MEMBER_HOME,
  PM_SCREEN_TYPE_YOGA_HOME,
  PM_SCREEN_TYPE_CENTERS,
  PM_SCREEN_TYPE_EVENTS_PLUS,
} from './types'

const config = getConfig()
const hubsTestActive = !!_get(config, ['features', 'hubsTest'])

/**
 * Get the page manager screen type by looking at the resolver state
 * @param {Object} options The options
 * @param {Map} options.auth The auth state
 * @param {Map} options.resolver The auth state
 * @returns {String} The page manager screen type if the url exist
 */
export function getPmScreenTypeFromResolver (options = {}) {
  const { auth = Map(), resolver = Map() } = options
  const path = resolver.get('path')
  if (isMemberHome(path, getAuthIsLoggedIn(auth))) {
    return PM_SCREEN_TYPE_MEMBER_HOME
  } if (isYogaPage(path)) {
    return PM_SCREEN_TYPE_YOGA_HOME
  } if (isTopics(path)) {
    return PM_SCREEN_TYPE_CENTERS
  } if (isEventsPage(path)) {
    return PM_SCREEN_TYPE_EVENTS_PLUS
  } if (hubsTestActive && isHubsAlternativeHealth(path)) {
    return SCREEN_TYPE_HUBS_ALTERNATIVE_HEALTH
  } if (hubsTestActive && isHubsAncientWisdom(path)) {
    return SCREEN_TYPE_HUBS_ANCIENT_WISDOM
  } if (hubsTestActive && isHubsPersonalDevelopment(path)) {
    return SCREEN_TYPE_HUBS_PERSONAL_DEVELOPMENT
  }
  return undefined
}

/**
 * Get the page manager screen data from state
 * @param {Object} options The options
 * @param {Map} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Map} The page manager screen data
 */
export function getPmScreenData (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'data'])
}

/**
 * Get the page manager data created
 * @param {Object} options The options
 * @param {Object} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Date|undefined} The page manager screen data created date
 */
export function getPmScreenDataCreated (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'dataCreated'])
}

/**
 * Get the page manager screen sections ids from state
 * @param {Object} options The options
 * @param {Map} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Map|undefined} The page manager screen data
 */
export function getPmScreenSectionSectionIds (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'data', 'sectionIds'], List())
}

/**
 * Get the page manager screen processing value
 * @param {Object} options The options
 * @param {Object} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Boolean|undefined} The page manager screen processing
 */
export function getPmScreenProcessing (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'processing'])
}

/**
 * Get the page manager screen error
 * @param {Object} options The options
 * @param {Object} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Error|undefined} The page manager screen error
 */
export function getPmScreenError (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'error'])
}

/**
 * Get the page manager screen error created
 * @param {Object} options The options
 * @param {Object} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Date|undefined} The page manager screen error created
 */
export function getPmScreenErrorCreated (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'errorCreated'])
}
